import experiment from 'experiment';

import * as helpIds from '#packages/helpIds';

import constants, { WORKSPACE_RIGHT_PANEL_NAMES } from '#packages/constants';
import { sections } from '#packages/util';
import {
  QuickEditApiKey,
  DesignerPanelsApiKey,
  EditorRestrictionsApiKey,
} from '#packages/apis';
import gfppDataUtils from '../../utils/gfppDataUtils';

import type { CompRef } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import type { GFPPAction } from '@wix/editor-component-types';

const { ACTIONS } = constants.ROOT_COMPS.GFPP;
const HEADER_SETTINGS_PANEL_NAME =
  'compPanels.panels.HeaderContainer.settingsPanel';

const toggleLayoutPanel = gfppDataUtils.getTogglePanelFn(ACTIONS.SETTINGS);

function getDesignerScanAction(): GFPPAction[] {
  return experiment.isOpen('se_addDesignerSection')
    ? [
        {
          label: 'Scan Header',
          shouldTranslate: false,
          onClick: (editorAPI: EditorAPI, compRefs: CompRef[]) => {
            const designerPanelsApi =
              editorAPI.host.getAPI(DesignerPanelsApiKey);
            designerPanelsApi.openDesignerScanPresetPanel(compRefs[0]);
          },
        },
      ]
    : [];
}

function getQuickEditAction(editorAPI: EditorAPI) {
  const action = {
    label: `gfpp_mainaction_edit_section_label`,
    onClick: (editorAPI: EditorAPI, compRefs: CompRef[], origin: string) => {
      const quickEditAPI = editorAPI.host.getAPI(QuickEditApiKey);
      quickEditAPI.openPanel({
        rootControlCompRef: compRefs[0],
        origin,
      });
    },
    panelName: WORKSPACE_RIGHT_PANEL_NAMES.QUICK_EDIT,
  };
  const isQuickEditAvailable = editorAPI.host
    .getAPI(QuickEditApiKey)
    .isQuickEditAvailable();

  return isQuickEditAvailable ? [action] : [];
}

function getDesignAction(editorAPI: EditorAPI) {
  return {
    label: editorAPI.zoomMode.isStageZoomMode()
      ? 'RightClick_Menu_Header_Zoom_Out_Mode_Zoom_Label'
      : 'gfpp_mainaction_header',
    isApplied: true,
    isSelected: gfppDataUtils.getPanelStateFn(ACTIONS.DESIGN),
    onClick: async (
      editorAPI: EditorAPI,
      compRef?: CompRef[],
      origin?: string,
      optionalPropsOverrides: Record<string, unknown> = {},
    ) => {
      if (editorAPI.zoomMode.isStageZoomMode()) {
        await editorAPI.zoomMode.exitZoomMode({
          biParams: { origin },
        });
      }
      const shouldShowOnlyAdvancedStylingDesignPanel =
        gfppDataUtils.siteSegmentBackgroundSupported(editorAPI, compRef?.[0]);

      optionalPropsOverrides.onlyAdvancedStyling =
        shouldShowOnlyAdvancedStylingDesignPanel;
      optionalPropsOverrides.startWithAdvanced =
        shouldShowOnlyAdvancedStylingDesignPanel;

      if (shouldShowOnlyAdvancedStylingDesignPanel) {
        const prevBiParams =
          typeof optionalPropsOverrides?.biParams === 'object'
            ? optionalPropsOverrides?.biParams
            : {};

        const biParams = {
          panel_name: constants.PANELS.ADVANCED_SITE_SEGMENT_DESIGN_PANEL,
          ...prevBiParams,
        };

        optionalPropsOverrides.biParams = biParams;
      }

      gfppDataUtils.getTogglePanelFn(ACTIONS.DESIGN)(
        editorAPI,
        compRef,
        origin,
        optionalPropsOverrides,
      );
    },
  };
}

export default {
  untranslatable: true,
  mainActions: (editorAPI: EditorAPI) => {
    const designAction = getDesignAction(editorAPI);
    const quickEditAction = getQuickEditAction(editorAPI);

    const editorRestrictionsApi = editorAPI.host.getAPI(
      EditorRestrictionsApiKey,
    );
    const isHeaderGfppVisible = editorRestrictionsApi.allowed(
      'gfpp_header.visible',
    );
    const designerScanAction = getDesignerScanAction();
    const designActionIfAvailable = isHeaderGfppVisible ? [designAction] : [];

    return [
      ...designerScanAction,
      ...designActionIfAvailable,
      ...quickEditAction,
    ];
  },
  enabledActions: () => {
    return sections.isSectionsEnabled()
      ? [[ACTIONS.SETTINGS], ACTIONS.HELP]
      : [[ACTIONS.SETTINGS], ACTIONS.DESIGN, ACTIONS.HELP];
  },
  mobileEnabledActions: () => {
    return [ACTIONS.HELP];
  },
  mobileMainActions: [
    {
      label: 'mobile_gfpp_mainaction_headerscroll',
      isApplied: true,
      panelName: HEADER_SETTINGS_PANEL_NAME,
      automationId: 'gfpp-button-layout',
      onClick: toggleLayoutPanel,
    },
  ],
  presetActions: () => {
    return {
      help: {
        helpId: helpIds.GFPP.HEADER,
        mobileHelpId: helpIds.GFPP.HEADER_SCROLL_MOBILE,
      },
      settings: {
        tooltip: sections.isSectionsEnabled()
          ? 'desktop_headerscroll_panel_title'
          : 'gfpp_tooltip_settings',
        panelName: HEADER_SETTINGS_PANEL_NAME,
      },
    };
  },
};
